import {
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { SearchNormal1 } from 'iconsax-reactjs';
import { Kbd, Modal, Spinner, cx } from '@/components/ui';
import styles from './GlobalSearch.module.css';

export interface SearchResult {
  id: string;
  /** Grouping key, e.g. "student", "staff", "class". */
  type: string;
  title: string;
  subtitle?: string;
  href: string;
}

export interface GlobalSearchProps {
  results: SearchResult[];
  loading?: boolean;
  /** Debounced query changes (fetch results in response). */
  onQueryChange: (query: string) => void;
  /** Navigate to the result (e.g. router.navigate(result.href)). */
  onSelect: (result: SearchResult) => void;
  /** Display names per result type; unknown types fall back to the raw type. */
  typeLabels?: Record<string, string>;
  /** Optional icon per result. */
  renderResultIcon?: (result: SearchResult) => ReactNode;
  placeholder?: string;
  /** Queries shorter than this show the hint instead of "no results". */
  minQueryLength?: number;
  debounceMs?: number;
  /** Register Ctrl/⌘+K. Default true. */
  enableShortcut?: boolean;
  triggerLabel?: string;
  className?: string;
}

function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
}

interface Group {
  type: string;
  items: { result: SearchResult; index: number }[];
}

function groupResults(results: SearchResult[]): Group[] {
  const groups: Group[] = [];
  const byType = new Map<string, Group>();
  results.forEach((result, index) => {
    let group = byType.get(result.type);
    if (!group) {
      group = { type: result.type, items: [] };
      byType.set(result.type, group);
      groups.push(group);
    }
    group.items.push({ result, index });
  });
  return groups;
}

/** Command-palette style global search: TopBar trigger + modal combobox. Data comes via props. */
export function GlobalSearch({
  results,
  loading = false,
  onQueryChange,
  onSelect,
  typeLabels,
  renderResultIcon,
  placeholder = 'Search…',
  minQueryLength = 2,
  debounceMs = 200,
  enableShortcut = true,
  triggerLabel = 'Search',
  className,
}: GlobalSearchProps) {
  const baseId = useId();
  const listId = `${baseId}-results`;
  const statusId = `${baseId}-status`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const apple = isApplePlatform();

  // Reset the highlighted result whenever a new result set arrives.
  const [resultsSeen, setResultsSeen] = useState(results);
  if (resultsSeen !== results) {
    setResultsSeen(results);
    setActiveIndex(0);
  }

  const openPalette = useEffectEvent(() => setOpen(true));

  useEffect(() => {
    if (!enableShortcut) return;
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enableShortcut]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  function changeQuery(next: string) {
    setQuery(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onQueryChange(next.trim()), debounceMs);
  }

  function choose(result: SearchResult | undefined) {
    if (!result) return;
    setOpen(false);
    onSelect(result);
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (results.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % results.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + results.length) % results.length);
        break;
      case 'Enter':
        event.preventDefault();
        choose(results[activeIndex]);
        break;
      default:
        break;
    }
  }

  const trimmed = query.trim();
  const showResults = trimmed.length >= minQueryLength && results.length > 0;
  let status: string;
  if (trimmed.length < minQueryLength) {
    status = `Type at least ${minQueryLength} characters to search.`;
  } else if (loading) {
    status = 'Searching…';
  } else if (results.length === 0) {
    status = `No results for “${trimmed}”.`;
  } else {
    status = `${results.length} result${results.length === 1 ? '' : 's'} available.`;
  }
  const groups = showResults ? groupResults(results) : [];
  const activeResult = showResults ? results[activeIndex] : undefined;

  return (
    <>
      <button
        type="button"
        className={cx(styles.trigger, className)}
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts={enableShortcut ? 'Control+K Meta+K' : undefined}
        onClick={() => setOpen(true)}
      >
        <SearchNormal1 size={16} color="currentColor" aria-hidden="true" />
        <span className={styles.triggerText}>{placeholder}</span>
        {enableShortcut && (
          <span className={styles.shortcut} aria-hidden="true">
            <Kbd>{apple ? '⌘' : 'Ctrl'}</Kbd>
            <Kbd>K</Kbd>
          </span>
        )}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={triggerLabel}
        hideTitle
        size="lg"
        position="top"
        initialFocusRef={inputRef}
        className={styles.dialog}
        bodyClassName={styles.body}
      >
        <div className={styles.inputRow}>
          <SearchNormal1 size={20} color="currentColor" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={triggerLabel}
            aria-expanded={showResults}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeResult ? `${baseId}-opt-${activeIndex}` : undefined}
            aria-describedby={statusId}
            className={styles.input}
            placeholder={placeholder}
            value={query}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => changeQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
          />
          {loading && <Spinner size="sm" decorative />}
          <Kbd className={styles.escHint} aria-hidden="true">
            Esc
          </Kbd>
        </div>
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Search results"
          className={styles.results}
        >
          {groups.map((group) => {
            const headingId = `${baseId}-group-${group.type}`;
            return (
              <div
                key={group.type}
                role="group"
                aria-labelledby={headingId}
                className={styles.group}
              >
                <div id={headingId} className={styles.groupLabel}>
                  {typeLabels?.[group.type] ?? group.type}
                </div>
                {group.items.map(({ result, index }) => (
                  // Keyboard interaction is on the combobox input (aria-activedescendant).
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <div
                    key={result.id}
                    id={`${baseId}-opt-${index}`}
                    data-index={index}
                    role="option"
                    tabIndex={-1}
                    aria-selected={index === activeIndex}
                    className={cx(styles.option, index === activeIndex && styles.optionActive)}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setActiveIndex(index)}
                    onClick={() => choose(result)}
                  >
                    {renderResultIcon && (
                      <span className={styles.optionIcon} aria-hidden="true">
                        {renderResultIcon(result)}
                      </span>
                    )}
                    <span className={styles.optionText}>
                      <span className={styles.optionTitle}>{result.title}</span>
                      {result.subtitle && (
                        <span className={styles.optionSubtitle}>{result.subtitle}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <p id={statusId} className={cx(styles.status, showResults && 'sr-only')} aria-live="polite">
          {status}
        </p>
      </Modal>
    </>
  );
}
